/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import {
  Calendar,
  CheckCircle,
  ClipboardList,
  Clock,
  FileText,
  MoreVertical,
  Package,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import AssignStaffModal from "../../../components/AdminSideComponents/bookings/AssignStaffModal";
import BookingActionsMenu from "../../../components/AdminSideComponents/bookings/BookingActionsMenu";
import BookingsTableSkeleton from "../../../components/AdminSideComponents/bookings/BookingsTableSkeleton";
import PaymentStatusUpdateModal from "../../../components/AdminSideComponents/bookings/PaymentStatusUpdateModal";
import RescheduleBookingModal from "../../../components/AdminSideComponents/bookings/RescheduleBookingModal";
import ViewBookingDetailsModal from "../../../components/AdminSideComponents/bookings/ViewBookingModal";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import Heading from "../../../components/AdminSideComponents/Heading";
import StatsCard from "../../../components/AdminSideComponents/StatCards";
import PaymentStatusBadge from "../../../components/common/PaymentStatusBadge";
import StatusBadge from "../../../components/common/StatusBadge";
import CancelBookingModal from "../../../components/StaffSideComponents/myJobs/CancelBookingModal";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { downloadInvoicePdf } from "../../../utils/downloadInvoicePdf ";
import {
  formatPaymentMethod,
  formatSlotDateTime,
} from "../../../utils/helpers";
import { roundAmount } from "../../../utils/money";
import { useNotifications } from "../../../context/NotificationContext";
import { useLocation, useNavigate } from "react-router-dom";

//  TRANSFORM SERVICE BOOKINGS (unchanged) 
const transformBookings = (bookings = []) => {
  if (!Array.isArray(bookings)) return [];
  return bookings.map((b) => ({
    id: b._id,
    raw: b,
    bookingId: b._id,
    customerName: b?.userId?.name,
    email: b?.userId?.email,
    phone: b?.userId?.phoneNumber,
    address: b?.userId?.address,
    services: b?.services || [],
    serviceNames: b?.services
      ?.map((s) => {
        const name = s?.serviceId?.name || "Service";
        return s?.brand ? `${name} (${s.brand})` : name;
      })
      .join(", "),
    totalAmount: roundAmount(b?.totalAmount, 0),
    paymentStatus: b?.payment?.status,
    paidAmount: b?.payment?.paidAmount || 0,
    finalPayableAmount: b?.finalPayableAmount || 0,
    couponCode: b?.coupon?.code || null,
    couponDiscount: b?.coupon?.discountApplied || 0,
    settledVia: b?.payment?.settledVia,
    paymentMethod: formatPaymentMethod(b?.payment?.method),
    bookingDate: b?.slotAvailabilityId?.date,
    slotTime: b?.slotSnapshot
      ? { start: b.slotSnapshot.startTime, end: b.slotSnapshot.endTime }
      : null,
    status: b?.status,
    cancelReason: b?.cancelReason,
    rescheduledDate: b?.rescheduledDate,
    staffId: b?.staffId?._id,
    assignedStaff: b?.staffId?.name || "---",
    createdAt: b?.createdAt,
    updatedAt: b?.updatedAt,
  }));
};

//  TRANSFORM PACKAGE BOOKINGS 
const transformPackageBookings = (bookings = []) => {
  if (!Array.isArray(bookings)) return [];
  return bookings.map((b) => ({
    id: b._id,
    raw: b,
    bookingId: b._id,
    customerName: b?.userId?.name,
    email: b?.userId?.email,
    phone: b?.userId?.phoneNumber,
    packageName: b?.packageSnapshot?.name || b?.packageId?.name || "Package",
    packageCategory: b?.packageSnapshot?.category || b?.packageId?.category || "",
    services: b?.packageSnapshot?.services || [],
    serviceNames: (b?.packageSnapshot?.services || []).join(", "),
    totalAmount: roundAmount(b?.totalAmount, 0),
    paymentStatus: b?.payment?.status,
    paidAmount: b?.payment?.paidAmount || 0,
    finalPayableAmount: b?.finalPayableAmount || 0,
    couponCode: b?.coupon?.code || null,
    couponDiscount: b?.coupon?.discountApplied || 0,
    settledVia: b?.payment?.settledVia,
    paymentMethod: formatPaymentMethod(b?.payment?.method),
    bookingDate: b?.slotAvailabilityId?.date,
    slotTime: b?.slotSnapshot
      ? { start: b.slotSnapshot.startTime, end: b.slotSnapshot.endTime }
      : null,
    status: b?.status,
    cancelReason: b?.cancelReason,
    rescheduledDate: b?.rescheduledDate,
    staffId: b?.staffId?._id,
    assignedStaff: b?.staffId?.name || "---",
    createdAt: b?.createdAt,
    updatedAt: b?.updatedAt,
  }));
};

const TABS = { SERVICE: "service", PACKAGE: "package" };

const Bookings = () => {
  const location = useLocation();
  const { admin, isAdmin, isManager } = useAdminAuth();
  const { unreadBookingIds } = useNotifications();
  const navigate = useNavigate();

  // ── SHARED STATE ──
  const [activeTab, setActiveTab] = useState(TABS.SERVICE);
  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // ── SERVICE BOOKING STATE ──
  const [stats, setStats] = useState({ total: 0, confirmed: 0, pending: 0, cancelled: 0 });
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // ── PACKAGE BOOKING STATE ──
  const [pkgStats, setPkgStats] = useState({ total: 0, confirmed: 0, pending: 0, cancelled: 0 });
  const [filteredPkgBookings, setFilteredPkgBookings] = useState([]);
  const [isPkgLoading, setIsPkgLoading] = useState(true);
  const [pkgCurrentPage, setPkgCurrentPage] = useState(1);
  const [pkgTotalPages, setPkgTotalPages] = useState(1);
  const [pkgTotalItems, setPkgTotalItems] = useState(0);

  // ── MODAL STATE ──
  const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(null);
  const [viewBooking, setViewBooking] = useState(null);
  const [viewBookingType, setViewBookingType] = useState(TABS.SERVICE);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [rescheduleBookingType, setRescheduleBookingType] = useState(TABS.SERVICE);
  const [assignStaffBooking, setAssignStaffBooking] = useState(null);
  const [assignStaffBookingType, setAssignStaffBookingType] = useState(TABS.SERVICE);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelBookingType, setCancelBookingType] = useState(TABS.SERVICE);
  const [paymentStatusBooking, setPaymentStatusBooking] = useState(null);
  const [paymentStatusBookingType, setPaymentStatusBookingType] = useState(TABS.SERVICE);
  const [selectedBookings, setSelectedBookings] = useState([]);

  const ITEMS_PER_PAGE = 10;

  // ── BRANCHES ──
  const fetchBranches = async () => {
    setBranchLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/branches/get-all`);
      const list = res?.data?.data || [];
      setBranches(list);
      if (list.length > 0) setSelectedBranch(list[0]._id);
    } catch (err) {
      console.error("Failed to fetch branches");
    } finally {
      setBranchLoading(false);
    }
  };

  // ── FETCH SERVICE BOOKINGS ──
  const fetchBookings = async () => {
    if (!selectedBranch) return;
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/service-bookings/get-all`,
        {
          params: {
            page: currentPage,
            search: debouncedSearchTerm || undefined,
            limit: ITEMS_PER_PAGE,
            branchId: selectedBranch,
          },
        },
      );
      const data = res?.data?.data ?? [];
      const pagination = res?.data?.pagination;
      setStats(res.data.stats);
      setFilteredBookings(transformBookings(data));
      setCurrentPage(pagination.page);
      setTotalPages(pagination.totalPages);
      setTotalItems(pagination.totalItems);
      localStorage.setItem("lastBookingVisit", new Date().toISOString());
      localStorage.setItem("lastBookingCount", data?.length?.toString());
    } catch (err) {
      console.error("Failed to fetch bookings", err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── FETCH PACKAGE BOOKINGS (branchId in query — picked up by branchMiddleware) ──
  const fetchPackageBookings = async () => {
    if (!selectedBranch) return;
    setIsPkgLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/package-bookings`,
        {
          params: {
            page: pkgCurrentPage,
            search: debouncedSearchTerm || undefined,
            limit: ITEMS_PER_PAGE,
            branchId: selectedBranch, // branchMiddleware reads this from query
          },
        },
      );
      const data = res?.data?.data ?? [];
      const pagination = res?.data?.pagination;
      setPkgStats(res.data.stats);
      setFilteredPkgBookings(transformPackageBookings(data));
      setPkgCurrentPage(pagination.page);
      setPkgTotalPages(pagination.totalPages);
      setPkgTotalItems(pagination.totalItems);
    } catch (err) {
      console.error("Failed to fetch package bookings", err);
    } finally {
      setIsPkgLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightId = params.get("highlight");

    if (!highlightId) return;

    const timer = setTimeout(() => {
      const el = document.getElementById(highlightId);

      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        el.classList.add("ring-2", "ring-green-400", "bg-green-100");

        setTimeout(() => {
          el.classList.remove("ring-2", "ring-green-400", "bg-green-100");
        }, 5000);
      }

      // remove param AFTER highlight duration
      setTimeout(() => {
        params.delete("highlight");

        navigate(
          {
            pathname: location.pathname,
            search: params.toString(),
          },
          { replace: true }
        );
      }, 5000);

    }, 300);

    return () => clearTimeout(timer);

  }, [location.search, filteredBookings]);

  // ── EFFECTS ──
  useEffect(() => {
    if (isAdmin) fetchBranches();
    else if (isManager && admin?.assignedBranch) setSelectedBranch(admin.assignedBranch._id);
  }, [isAdmin, isManager, admin]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => { fetchBookings(); }, [currentPage, selectedBranch, debouncedSearchTerm]);
  useEffect(() => { fetchPackageBookings(); }, [pkgCurrentPage, selectedBranch, debouncedSearchTerm]);

  useEffect(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSelectedBookings([]);
    setIsActionDropdownOpen(null);
  }, [activeTab]);

  // ── HANDLERS ──
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    setPkgCurrentPage(1);
  };

  const completedSelections = selectedBookings.filter((b) => b.status === "completed");
  const canDownloadInvoices = completedSelections.length > 0;

  const toggleBookingSelection = (booking) => {
    setSelectedBookings((prev) => {
      const exists = prev.some((b) => b.id === booking.id);
      return exists ? prev.filter((b) => b.id !== booking.id) : [...prev, booking];
    });
  };

  const isBookingSelected = (bookingId) => selectedBookings.some((b) => b.id === bookingId);

  const handleDownloadInvoice = async () => {
    if (!canDownloadInvoices) return;
    for (const booking of completedSelections) await downloadInvoicePdf(booking);
  };

  const downloadInvoiceBtn = () => (
    <button
      onClick={handleDownloadInvoice}
      disabled={!canDownloadInvoices}
      className={`px-4 py-2 rounded-lg flex items-center gap-2 font-medium transition
        ${canDownloadInvoices ? "bg-green-600 text-white hover:bg-green-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
    >
      <FileText size={18} />
      Download Invoice
      {canDownloadInvoices && (
        <span className="text-xs bg-white/20 px-2 py-0.5 rounded">{completedSelections.length}</span>
      )}
    </button>
  );

  const handlePaymentStatusClick = (booking, type) => {
    setPaymentStatusBookingType(type);
    setPaymentStatusBooking(booking);
  };

  const handleBookingAction = (action, booking, type) => {
    setIsActionDropdownOpen(null);
    switch (action) {
      case "view":
        setViewBookingType(type);
        setViewBooking(booking.raw);
        break;
      case "download_invoice":
        downloadInvoicePdf(booking);
        break;
      case "assign_staff":
      case "replace_staff":
        setAssignStaffBookingType(type);
        setAssignStaffBooking(booking.raw);
        break;
      case "reschedule":
        setRescheduleBookingType(type);
        setRescheduleBooking(booking.raw);
        break;
      case "cancel":
        setCancelBookingType(type);
        setCancelBooking(booking.raw);
        break;
      default:
        break;
    }
  };

  const isServiceTab = activeTab === TABS.SERVICE;
  const activeBookings = isServiceTab ? filteredBookings : filteredPkgBookings;
  const activeLoading = isServiceTab ? isLoading : isPkgLoading;
  const activeStats = isServiceTab ? stats : pkgStats;
  const activeTotalPages = isServiceTab ? totalPages : pkgTotalPages;
  const activeTotalItems = isServiceTab ? totalItems : pkgTotalItems;
  const activePage = isServiceTab ? currentPage : pkgCurrentPage;
  const setActivePage = isServiceTab ? setCurrentPage : setPkgCurrentPage;
  const fetchActive = isServiceTab ? fetchBookings : fetchPackageBookings;

  return (
    <div className="bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      <div className="py-2">
        <Breadcrumb title1="Bookings" title2="Booking Management" />
      </div>

      {/* BRANCH SWITCHER */}
      {isAdmin && (
        <div className="mb-6 bg-white rounded-lg shadow-md border p-2">
          {branchLoading ? (
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {branches.map((b) => (
                <button
                  key={b._id}
                  onClick={() => { setSelectedBranch(b._id); setCurrentPage(1); setPkgCurrentPage(1); }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${selectedBranch === b._id
                    ? "bg-gradient-to-r from-accentTextColor to-accentTextColor/80 text-white shadow"
                    : "bg-gray-100 text-gray-600 hover:bg-brandAccentColor/40"}`}
                >
                  {b.branchName}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-6 bg-white rounded-md shadow-lg p-6">

        {/* TAB SWITCHER */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab(TABS.SERVICE)}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px ${activeTab === TABS.SERVICE
              ? "border-accentTextColor text-accentTextColor"
              : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            <ClipboardList size={16} />
            Service Bookings
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${activeTab === TABS.SERVICE ? "bg-accentTextColor/10 text-accentTextColor" : "bg-gray-100 text-gray-500"}`}>
              {stats.total}
            </span>
          </button>

          <button
            onClick={() => setActiveTab(TABS.PACKAGE)}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px ${activeTab === TABS.PACKAGE
              ? "border-accentTextColor text-accentTextColor"
              : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            <Package size={16} />
            Package Bookings
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${activeTab === TABS.PACKAGE ? "bg-accentTextColor/10 text-accentTextColor" : "bg-gray-100 text-gray-500"}`}>
              {pkgStats.total}
            </span>
          </button>
        </div>

        {/* STATS */}
        <StatsCard
          title1="Total Bookings" icon1={Calendar} value1={activeStats.total}
          title2="Confirmed Bookings" icon2={CheckCircle} value2={activeStats.confirmed}
          title3="Pending Bookings" icon3={Clock} value3={activeStats.pending}
          title4="Cancelled Bookings" icon4={XCircle} value4={activeStats.cancelled}
        />

        <Heading
          onSearch={handleSearch}
          searchPlaceholder="Search name, mobile, or booking ID"
          customActions={downloadInvoiceBtn}
        />

        {/* TABLE */}
        <div className="overflow-x-auto no-scrollbar min-h-[320px]">
          {isServiceTab ? (
            /* ════ SERVICE BOOKINGS TABLE ════ */
            <table className="w-full table-fixed border border-gray-200 text-sm">
              <thead className="bg-[#EDB5A4] text-xs uppercase text-gray-700">
                <tr>
                  <th className="w-8 text-center my-auto"></th>
                  <th className="p-2 w-12 text-center">#</th>
                  <th className="p-3 w-36 text-left">Customer</th>
                  <th className="p-3 w-36 text-left">Services</th>
                  <th className="p-3 w-24 text-left">Amount</th>
                  <th className="p-3 w-36 text-left">Payment Status</th>
                  <th className="p-3 w-36 text-center">Booking Date</th>
                  <th className="py-3 pl-8 w-32 text-left">Status</th>
                  <th className="p-3 w-28 text-left">Staff</th>
                  <th className="p-3 w-16 text-left">Action</th>
                </tr>
              </thead>

              {activeLoading ? (
                <BookingsTableSkeleton rows={ITEMS_PER_PAGE} />
              ) : activeBookings.length === 0 ? (
                <EmptyState />
              ) : (
                <tbody className="divide-y">
                  {activeBookings.map((b, index) => {
                    const rowCount = activeBookings.length;
                    const shouldOpenTop = rowCount > 4 && index >= rowCount - 2;
                    return (
                      <tr
                        id={b.id}
                        key={b.id}
                        className={` hover:bg-gray-50 transition-all duration-300 ${unreadBookingIds?.includes(b.id) ? "bg-green-50 border-l-4 border-green-500 shadow-sm" : ""} `}>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            disabled={b.status !== "completed"}
                            checked={isBookingSelected(b.id)}
                            onChange={() => toggleBookingSelection(b)}
                            className="w-4 h-4 rounded border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="p-3 text-center">
                          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                        </td>
                        <td
                          onClick={() => { setViewBookingType(TABS.SERVICE); setViewBooking(b.raw); setIsActionDropdownOpen(null); }}
                          className="p-3 flex flex-col gap-0.5 cursor-pointer"
                        >
                          <span className="line-clamp-1">{b.customerName}</span>
                          <span className="text-xs text-secondaryTextColor">{b.phone}</span>
                        </td>
                        <td
                          title={b.services.map((s) => `${s.serviceId?.name} × ${s.qty} = ₹${roundAmount(s.finalPrice * s.qty)}`).join("\n")}
                          onClick={() => { setViewBookingType(TABS.SERVICE); setViewBooking(b.raw); setIsActionDropdownOpen(null); }}
                          className="p-3 cursor-pointer  truncate"
                        >
                          {b.serviceNames}
                        </td>
                        <td
                          title={b.services.map((s) => `${s.serviceId?.name} × ${s.qty} = ₹${roundAmount(s.finalPrice * s.qty)}`).join("\n")}
                          className="p-3 font-medium"
                        >
                          <div className="flex flex-col leading-tight">
                            <span className="font-semibold">₹{b.totalAmount}</span>
                            {/* <span className="font-semibold">₹{b.finalPayableAmount || b.totalAmount}</span> */}
                            {b.paymentStatus === "partially_paid" && (
                              <span className="text-[0.64rem] flex flex-col gap-0.5">
                                <span className=" text-green-700">Paid ₹{b.paidAmount}</span>
                                <span className=" text-red-600">Due ₹{(b.finalPayableAmount || b.totalAmount) - b.paidAmount}</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 font-medium">
                          <button
                            onClick={() => handlePaymentStatusClick(b.raw, TABS.SERVICE)}
                            className="hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 outline-none"
                          >
                            <PaymentStatusBadge status={b?.paymentStatus} settledVia={b?.settledVia} />
                          </button>
                        </td>
                        <td className="p-1 text-center">
                          {(() => {
                            const { date, time } = formatSlotDateTime(b.bookingDate, b.slotTime);
                            return (
                              <div className="flex flex-col">
                                <span className="font-medium text-[0.64rem] uppercase">{time}</span>
                                <span className="text-xs text-secondaryTextColor">{date}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="p-3"><StatusBadge status={b.status} /></td>
                        <td className="p-3 font-medium">{b.assignedStaff}</td>
                        <td className="relative py-4 text-center">
                          <button
                            onClick={() => setIsActionDropdownOpen((prev) => prev === b.id ? null : b.id)}
                            className="p-2 rounded-lg hover:bg-gray-200 transition"
                          >
                            <MoreVertical size={20} className="text-gray-600" />
                          </button>
                          {isActionDropdownOpen === b.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setIsActionDropdownOpen(null)} />
                              <BookingActionsMenu
                                booking={b}
                                placement={shouldOpenTop ? "top" : "bottom"}
                                onAction={(action, booking) => handleBookingAction(action, booking, TABS.SERVICE)}
                              />
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          ) : (
            /* ════ PACKAGE BOOKINGS TABLE ════ */
            <table className="w-full table-fixed border border-gray-200 text-sm">
              <thead className="bg-[#EDB5A4] text-xs uppercase text-gray-700">
                <tr>
                  <th className="w-8 text-center my-auto"></th>{/* checkbox col */}
                  <th className="p-2 w-12 text-center">#</th>
                  <th className="p-3 w-36 text-left">Customer</th>
                  <th className="p-3 w-40 text-left">Package</th>
                  <th className="p-3 w-44 text-left">Included Services</th>
                  <th className="p-3 w-24 text-left">Amount</th>
                  <th className="p-3 w-36 text-left">Payment Status</th>
                  <th className="p-3 w-36 text-center">Booking Date</th>
                  <th className="py-3 pl-8 w-32 text-left">Status</th>
                  <th className="p-3 w-28 text-left">Staff</th>
                  <th className="p-3 w-16 text-left">Action</th>
                </tr>
              </thead>

              {activeLoading ? (
                <BookingsTableSkeleton rows={ITEMS_PER_PAGE} />
              ) : activeBookings.length === 0 ? (
                <EmptyState label="No package bookings found" />
              ) : (
                <tbody className="divide-y">
                  {activeBookings.map((b, index) => {
                    const rowCount = activeBookings.length;
                    const shouldOpenTop = rowCount > 4 && index >= rowCount - 2;
                    return (
                      <tr
                        key={b.id} className="hover:bg-gray-50">
                        <td className="text-center">
                          {/* Checkbox — same as service bookings */}
                          <input
                            type="checkbox"
                            disabled={b.status !== "completed"}
                            checked={isBookingSelected(b.id)}
                            onChange={() => toggleBookingSelection(b)}
                            className="w-4 h-4 rounded border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="p-3 text-center">
                          {(pkgCurrentPage - 1) * ITEMS_PER_PAGE + index + 1}
                        </td>
                        <td
                          onClick={() => { setViewBookingType(TABS.PACKAGE); setViewBooking(b.raw); setIsActionDropdownOpen(null); }}
                          className="p-3 flex flex-col gap-0.5 cursor-pointer"
                        >
                          <span className="line-clamp-1">{b.customerName}</span>
                          <span className="text-xs text-secondaryTextColor">{b.phone}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800 line-clamp-1">{b.packageName}</span>
                            {b.packageCategory && (
                              <span className="text-xs text-gray-400 uppercase tracking-wide">{b.packageCategory}</span>
                            )}
                          </div>
                        </td>
                        <td title={b.serviceNames} className="p-3 max-w-xs truncate text-gray-600">
                          {b.serviceNames || "—"}
                        </td>
                        <td className="p-3 font-medium">
                          <div className="flex flex-col leading-tight">
                            <span className="font-semibold">₹{b.finalPayableAmount || b.totalAmount}</span>
                            {b.paymentStatus === "partially_paid" && (
                              <>
                                <span className="text-[11px] text-green-700">Paid ₹{b.paidAmount}</span>
                                <span className="text-[11px] text-red-600">Due ₹{(b.finalPayableAmount || b.totalAmount) - b.paidAmount}</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="p-3 font-medium">
                          <button
                            onClick={() => handlePaymentStatusClick(b.raw, TABS.PACKAGE)}
                            className="hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 outline-none"
                          >
                            <PaymentStatusBadge status={b?.paymentStatus} settledVia={b?.settledVia} />
                          </button>
                        </td>
                        <td className="p-1 text-center">
                          {(() => {
                            const { date, time } = formatSlotDateTime(b.bookingDate, b.slotTime);
                            return (
                              <div className="flex flex-col">
                                <span className="font-medium text-[0.64rem] uppercase">{time}</span>
                                <span className="text-xs text-secondaryTextColor">{date}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="p-3"><StatusBadge status={b.status} /></td>
                        <td className="p-3 font-medium">{b.assignedStaff}</td>
                        <td className="relative py-4 text-center">
                          <button
                            onClick={() => setIsActionDropdownOpen((prev) => prev === b.id ? null : b.id)}
                            className="p-2 rounded-lg hover:bg-gray-200 transition"
                          >
                            <MoreVertical size={20} className="text-gray-600" />
                          </button>
                          {isActionDropdownOpen === b.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setIsActionDropdownOpen(null)} />
                              <BookingActionsMenu
                                booking={b}
                                placement={shouldOpenTop ? "top" : "bottom"}
                                onAction={(action, booking) => handleBookingAction(action, booking, TABS.PACKAGE)}
                              />
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          )}
        </div>

        {/* PAGINATION */}
        {activeTotalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <p className="text-sm text-gray-600">
              Page {activePage} of {activeTotalPages} • {activeTotalItems} bookings
            </p>
            <div className="flex gap-2">
              <button
                disabled={activePage === 1}
                onClick={() => setActivePage((p) => p - 1)}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >Prev</button>
              <button
                disabled={activePage === activeTotalPages}
                onClick={() => setActivePage((p) => p + 1)}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* ── MODALS ── */}
      {viewBooking && (
        <ViewBookingDetailsModal
          booking={viewBooking}
          bookingType={viewBookingType}
          onClose={() => setViewBooking(null)}
        />
      )}
      {rescheduleBooking && (
        <RescheduleBookingModal
          booking={rescheduleBooking}
          bookingType={rescheduleBookingType}
          branchId={selectedBranch}
          onClose={() => setRescheduleBooking(null)}
          onSuccess={() => { fetchActive(); setRescheduleBooking(null); }}
        />
      )}
      <CancelBookingModal
        isOpen={!!cancelBooking}
        booking={cancelBooking}
        bookingType={cancelBookingType}
        onClose={() => setCancelBooking(null)}
        onSuccess={fetchActive}
      />
      <AssignStaffModal
        isOpen={!!assignStaffBooking}
        booking={assignStaffBooking}
        bookingType={assignStaffBookingType}
        branchId={selectedBranch}
        onClose={() => setAssignStaffBooking(null)}
        onSuccess={fetchActive}
      />
      <PaymentStatusUpdateModal
        isOpen={!!paymentStatusBooking}
        booking={paymentStatusBooking}
        bookingType={paymentStatusBookingType}
        onClose={() => setPaymentStatusBooking(null)}
        onSuccess={fetchActive}
      />
    </div>
  );
};

const EmptyState = ({ label = "No bookings found" }) => (
  <tbody>
    <tr>
      <td colSpan={10} className="py-16 text-center">
        <div className="flex flex-col items-center gap-4 text-gray-500">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <ClipboardList className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700">{label}</h3>
        </div>
      </td>
    </tr>
  </tbody>
);

export default Bookings;
